"use client"

import Button from '@/components/atoms/Button/Button';
import Dot from '@/components/atoms/Dot/Dot';
import Tab from '@/components/atoms/Tab/Tab';
import useIsMobile from '@/core/hooks/useIsMobile';
import clsx from 'clsx';
import Link from 'next/link';
import { FC } from 'react';

type Props = {
    theme?: 'dark' | 'light';
    imageUrl?: string;
    highlightValue?: string;
    highlightLabel?: string;
    author?: string;
    readTime?: string;
    title?: string;
    description?: string;
    tags?: string[];
    buttonLabel?: string;
    buttonLink?: string;
    isVideo?: boolean;
    bgColor?: string;
};

export const BlogCard: FC<Props> = ({
    theme = 'light',
    imageUrl,
    highlightValue,
    highlightLabel,
    author,
    readTime,
    title,
    description,
    tags,
    buttonLabel,
    buttonLink = '#',
    isVideo = false,
    bgColor = 'background-color-transparent',
}) => {
    const textColor = bgColor === 'background-color-white' ? 'text-[var(--black-color-40)]' : 'text-[var(--text-color)]';
    const titleColor = bgColor === 'background-color-white' ? 'text-[var(--text-color-default)]' : 'text-[var(--text-white-default)]';
    const bgColorTag = bgColor === 'background-color-white' ? 'bg-[#0B0C0B1A]' : 'bg-[#F3F3F31A]';
    const colorTag = bgColor === 'background-color-white' ? 'text-[var(--text-color-default)]' : 'text-[var(--text-white-default)]';
    const colorButton = bgColor === 'background-color-white' ? 'text-[var(--text-color-default)]' : 'text-[var(--brand-color)]';
    const isMobile = useIsMobile(768);
    const isTablet = useIsMobile(1024);
    const widthImage = isMobile ? 'w-[320px] h-[192.59px]' : isTablet ? 'max-w-[400px] h-[216.67px]' : 'max-w-[400px] h-[240.74px]'
    const widthContainer = isMobile ? 'w-[320px]' : isTablet ? 'max-w-[400px] ' : 'max-w-[400px]'

    const backgroundColorClass = {
        'background-color-white': 'bg-white rounded-[20px]',
        'background-color-transparent': 'bg-transparent rounded-tl-[20px] rounded-tr-[20px]',
        'background-color-volt-8': 'bg-[var(--background-color-volt-8)] rounded-[20px]',
        'background-color-tangelo-7': 'bg-[var(--background-color-tangelo-7)] rounded-[20px]',
        'background-color-dimgrey-7': 'bg-[var(--background-color-dimgrey-7)] rounded-[20px]',
    }[bgColor];
    const paddingContentStyle = bgColor === 'background-color-transparent' || bgColor === null ? 'pt-4' : 'p-5';
    const hasHightLight = highlightValue && highlightLabel;

    return (
        <div className={clsx("blog-card flex-shrink-0 h-full overflow-hidden flex flex-col", backgroundColorClass, widthContainer)}>
            <Link scroll={false} prefetch={false} href={buttonLink || '#'} className="unstyled-link">
                {/* Image with overflow container */}
                <div className={clsx("relative w-full overflow-hidden", bgColor === 'background-color-transparent' && 'rounded-[20px]')}>
                    <div
                        style={{
                            height: hasHightLight ? `calc(100% - 64px)` : undefined,
                            borderRadius: hasHightLight ? '20px 20px 0 0' : undefined,
                        }}
                        className={clsx(widthImage, bgColor === 'background-color-transparent' && 'rounded-[20px]')}
                    >
                        <img
                            style={{
                                borderRadius: hasHightLight ? '20px 20px 0 0' : undefined,
                            }}
                            src={imageUrl} alt={title} className={clsx("w-full h-full object-cover", bgColor === 'background-color-transparent' && 'rounded-[20px]')}
                        />
                    </div>

                    {/* Icon videoplay */}
                    {isVideo && (
                        <Button
                            customClassName="absolute right-2 bottom-2 !border-none"
                            size='medium'
                            variant='black-primary'
                            icon={<img src='/images/play.svg' alt='icon-play' />}
                        />
                    )}

                    {/* Highlight */}
                    {hasHightLight && (
                        <div className="h-fit absolute bottom-0 right-0 left-0 bg-[#E7F36F] px-5 py-3 flex items-center gap-3">
                            <span style={{ fontSize: 'var(--font-size-huge)', fontFamily: 'var(--font-bw-gradual)' }} className="text-[var(--text-color-highlight)] font-[var(--font-weight-extra-bold)] leading-[var(--line-height-huge)] uppercase">{highlightValue}</span>
                            <span style={{ fontSize: 'var(--font-size-medium)' }} className="text-[var(--text-color-highlight)] font-[var(--font-weight-regular)] leading-[var(--line-height-medium)]">{highlightLabel}</span>
                        </div>
                    )
                    }
                </div>

                {/* Content */}
                <div className={clsx("flex flex-col gap-3 flex-1 h-full", paddingContentStyle)}>
                    <div className='flex gap-2 items-center'>
                        {author && (
                            <span style={{ fontSize: 16 }} className={clsx("leading-[var(--line-height-medium)] font-[var(--font-weight-regular)]", textColor)}>
                                {author}
                            </span>
                        )}
                        {author && readTime && <Dot />}
                        {readTime && (
                            <span style={{ fontSize: 14 }} className={clsx("leading-[var(--line-height-medium)] font-[var(--font-weight-regular)]", textColor)}>
                                {readTime}
                            </span>
                        )}
                    </div>

                    <div className="flex flex-col gap-1 flex-1">
                        <h5 className={clsx(titleColor, 'line-clamp-2')}>{title}</h5>
                        <span style={{ fontSize: 16 }} className={clsx("leading-[var(--line-height-medium)] font-[var(--font-weight-regular)] line-clamp-3 flex-1", textColor)}>{description}</span>
                    </div>

                    {/* Tags */}
                    {tags && tags.length > 0 && (
                        <div className="flex flex-wrap gap-[6px]">
                            {tags.map((tag, index) => (
                                <Tab key={index} value={tag} className={clsx(colorTag, bgColorTag)} />
                            ))}
                        </div>
                    )}

                    {/* CTA */}
                    {buttonLabel && (
                        <div style={{ fontSize: 16 }} className={clsx('blog-card-cta w-fit h-8 py-1 leading-[var(--line-height-medium)] font-[var(--font-weight-bold)] flex flex-1 items-end', colorButton)}>
                            {buttonLabel}
                        </div>
                    )}
                </div>
            </Link>
        </div>
    );
};
